import {Component, OnDestroy, OnInit} from '@angular/core';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {Subject, forkJoin, of} from 'rxjs';
import {takeUntil, tap, filter, catchError} from 'rxjs/operators';
import {DBService} from 'src/app/core/backend-adapter/db.service';
import {FILTER_TYPES, IFilterData} from 'src/app/core/feature-modules/table-filter/table-filter.interfaces';
import {TableFilterService} from 'src/app/core/feature-modules/table-filter/table-filter.service';
import {NotifyService} from 'src/app/core/layouts/notifications/notify/notify.service';
import {ACTIVITY_STATUS} from '../abp-tabs.constants';
import {ABPService} from '../abp.service';
import {SelectPostTypeComponent} from './components/abp-post-create/select-post-type/select-post-type.component';
import {SuccessComponent} from './components/abp-post-create/success/success.component';
import {LazyLoadEvent} from 'primeng/api';
import { SYSTEM_COLORS } from 'src/app/core/feature-modules/whitelabel/style-changer/styles/colors/system-colors.constants';
import { IFitersObject } from 'src/app/review-management/review-management.interfaces';
import { ABPFiltersDateService } from '../services/abp-filters-date-service/abp-filters-date-service';
import { SocketService } from 'src/app/core/backend-adapter/socket.service';
import { IAccount } from 'src/app/acct-comps/accounts.interfaces';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { BroadcastService } from 'src/app/core/backend-adapter/broadcast.service';
import { AbpPostEditComponent } from './components/abp-post-edit/abp-post-edit.component';
import { AbpPostDeleteComponent } from './components/abp-post-delete/abp-post-delete.component';
import { AbpPostLocationsComponent } from './components/abp-post-locations/abp-post-locations.component';
import { EventPostComponent } from './components/abp-post-create/event-post/event-post.component';
import RZLib from 'src/app/core/backend-adapter/db2/RZ-lib.helpers';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { generateJWTToken } from 'src/app/core/helpers/jwt-token.utils';
import { AbpLocationsFilterService } from '../services/abp-locations-filter.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-abp-post',
  templateUrl: './abp-tab-post.component.html',
  styleUrls: ['./abp-tab-post.component.scss'],
  providers: [DialogService],
})
export class AbpTabPostComponent implements OnInit, OnDestroy {
  SYSTEM_COLORS = SYSTEM_COLORS;
  keyword = '';
  postData = [];
  tableData = [];
  selectedRows = [];
  filterData: IFilterData[];
  chipsArray: string[] = [];
  tooltipText = 'Coming soon. Please reach out to support if you need to edit a post.';
  activityStatus: typeof ACTIVITY_STATUS = ACTIVITY_STATUS;
  totalLocationsCount;
  allLocations;
  allPostsData;
  initialPostsData;
  postTrends;
  postLoading = true;
  errorImg = '';
  createPostRef: DynamicDialogRef;
  totalPostsCount: number;
  offset = 0;
  pageSize = 25;
  sortOrder = '';
  filters: IFitersObject = { };

  private unsubscribe$ = new Subject<void>();
  private account: IAccount;
  /** Tracks last account we loaded for; used to avoid duplicate load on init (table lazy load will do first load). */
  private lastLoadedAccountId: number | null = null;

  private readonly JWT_SECRET = '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a';
  private readonly SHOWCASES_API_URL = 'https://io.renderseo.com/apple/showcasecreatives';
  /** Base URL for fetching showcase status by creative_id (GET .../showcases?creative_id=). */
  private readonly SHOWCASES_STATUS_API_BASE = 'https://io.renderseo.com/apple/showcases';
  /** POST deactivate/showcase action. */
  private readonly SHOWCASES_ACTION_API_URL = 'https://io.renderseo.com/apple/showcases/action';
  /** Status per creative_id from GET /apple/showcases?creative_id=... (showcase_status). */
  statusByCreativeId: Record<string, string> = {};
  /** showcase_id per creative_id from GET /apple/showcases?creative_id=... (used for action API). */
  private showcaseIdByCreativeId: Record<string, string | number> = {};
  /** Set while deactivate request is in progress (creative_id) to avoid double-submit. */
  deactivatingShowcaseId: string | number | null = null;

  constructor(
    private dialogService: DialogService,
    private tableFilterService: TableFilterService,
    public dbService: DBService,
    private abpService: ABPService,
    private notifyService: NotifyService,
    private broadcastService: BroadcastService,
    private sessionService: SessionService,
    private abpFiltersDateService: ABPFiltersDateService,
    private socketService: SocketService,
    private http: HttpClient,
    private locationsFilterService: AbpLocationsFilterService
  ) {
  }

  ngOnInit(): void {
    // Set initial account
    if (this.sessionService.currAccount) {
      this.account = this.dbService.Account.findByIdent(this.sessionService.currAccount);
    }

    // Subscribe to account changes (same pattern as listings: use selected account's _id)
    this.sessionService.getSelectedAccount$()
      .pipe(
        filter(account => account != null),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((account) => {
        this.account = account;
        const accountId = account?._id;
        if (!accountId) {
          return;
        }
        const accountChanged = this.lastLoadedAccountId !== null && this.lastLoadedAccountId !== accountId;
        if (accountChanged) {
          this.loadData();
        }
        this.lastLoadedAccountId = accountId;
      });

    this.filterData = [
      {
        type: FILTER_TYPES.MULTI,
        title: 'ATTRIBUTES',
        options: ['Scheduled', 'Published', 'Unpublished'],
      },
      // {
      //   type: FILTER_TYPES.MULTI,
      //   title: 'POST',
      //   options: ['STANDARD', 'EVENT', 'OFFER', 'ALERT'],
      // },
      {
        type: FILTER_TYPES.DATE,
        title: 'DATE',
        options: ['This month', 'Last month'],
      },
    ];

    // Don't load locations count here - it will be loaded in parallel with showcases in loadData()
    // This ensures it loads when account is available and in parallel with the list
    this.setSubscriptions();
  }

  private loadTotalLocationsCount(): void {
    this.abpService.getTotalLocationsCount()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (count) => {
          this.totalLocationsCount = count || 0;
        },
        (err) => {
          console.warn('Error loading total locations count:', err);
          this.totalLocationsCount = 0;
        }
      );
  }

  lazyLoadData(event: LazyLoadEvent) {
    this.offset = event.first;
    this.pageSize = event.rows;
    if (event.sortField) {
      this.sortOrder = `${event.sortField}${event.sortOrder < 0 ? ' DESC' : ''}`;
    }
    return this.loadData();
  }

  loadData() {
    // Get account and user the same way as listings: selected account _id and current user login _id
    const accountId = this.account?._id;
    const userId = this.sessionService.loginObj?._id;

    if (!userId) {
      console.error('User ID not available for API call', { userId });
      this.notifyService.error('Failed to load showcases: Authentication required');
      this.postLoading = false;
      return;
    }

    // Generate JWT token for authentication (same pattern as listings.service.ts)
    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000,
    };
    const jwtToken = generateJWTToken(payloadObj, this.JWT_SECRET);
    const headers = new HttpHeaders()
      .set('Authorization', `${jwtToken}`)
      .set('Content-Type', 'application/json');

    // API URL with auth token only (no account_id)
    const apiUrl = this.SHOWCASES_API_URL;

    // Preload locations in parallel while loading showcases
    // This will cache the locations so the create modal opens instantly
    this.preloadLocationsForCreateModal(userId);

    // Load showcases and locations count in parallel
    const showcasesRequest = this.http.get<any>(apiUrl, { headers });
    const locationsCountRequest = this.abpService.getTotalLocationsCount().pipe(
      catchError((err) => {
        console.warn('Error loading total locations count:', err);
        return of(0); // Return 0 on error so the showcases still load
      })
    );

    // Use forkJoin to load both in parallel
    forkJoin({
      showcases: showcasesRequest,
      locationsCount: locationsCountRequest
    })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        ({ showcases: data, locationsCount }) => {
          // Set locations count from parallel request
          this.totalLocationsCount = locationsCount || 0;

          console.log('Showcases API response:', data);
          console.log('Response keys:', data ? Object.keys(data) : 'null/undefined');
          if (data && data.items) {
            console.log('data.items content:', data.items);
          }
          
          // Handle the response data
          let showcasesData = [];
          if (Array.isArray(data)) {
            showcasesData = data;
          } else if (data && data.items && Array.isArray(data.items)) {
            // API returns {items: [...]}
            showcasesData = data.items;
          } else if (data && data.data && Array.isArray(data.data)) {
            showcasesData = data.data;
          } else if (data && data.showcases && Array.isArray(data.showcases)) {
            showcasesData = data.showcases;
          } else {
            showcasesData = [];
          }
          if (showcasesData.length > 0) {
            console.log('=== SHOWCASE DETAILS ===');
            showcasesData.forEach((showcase, index) => {
            });
            console.log('\n=== END SHOWCASE DETAILS ===');
          } else {
            console.warn('⚠️ No showcases found in the response');
          }

          // Apply pagination to API data
          const startIndex = this.offset || 0;
          const endIndex = startIndex + this.pageSize;
          const paginatedData = showcasesData.slice(startIndex, endIndex);

          // Apply sorting if needed
          let sortedData = [...paginatedData];
          if (this.sortOrder) {
            const [field, direction] = this.sortOrder.split(' ');
            sortedData.sort((a, b) => {
              const aVal = a[field];
              const bVal = b[field];
              if (direction === 'DESC') {
                return bVal > aVal ? 1 : -1;
              }
              return aVal > bVal ? 1 : -1;
            });
          }

          // Apply filters if needed
          let filteredData = sortedData;
          if (this.chipsArray.length > 0) {
            filteredData = sortedData.filter(item => {
              // Filter by status - handle both old format (_status) and new format (status)
              if (this.filters['ATTRIBUTES']) {
                const itemStatus = item.status || item._status;
                const statusMatch = this.filters['ATTRIBUTES'].some(filter => {
                  switch (filter) {
                    case 'Scheduled':
                      // Old format: AP, UP, DP | New format: PENDING_REVIEW, SCHEDULED, etc.
                      return ['AP', 'UP', 'DP'].includes(item._status) || 
                             ['PENDING_REVIEW', 'SCHEDULED'].includes(item.status);
                    case 'Published':
                      // Old format: A | New format: PUBLISHED, ACTIVE, etc.
                      return item._status === 'A' || 
                             ['PUBLISHED', 'ACTIVE', 'LIVE'].includes(item.status);
                    case 'Unpublished':
                      // Old format: U | New format: UNPUBLISHED, INACTIVE, etc.
                      return item._status === 'U' || 
                             ['UNPUBLISHED', 'INACTIVE'].includes(item.status);
                    default:
                      return false;
                  }
                });
                if (!statusMatch) return false;
              }
              return true;
            });
          }

          // Apply keyword search if needed
          if (this.keyword) {
            filteredData = filteredData.filter(item => {
              const keywordLower = this.keyword.toLowerCase();
              const headlineText = item.showcaseCreativeDetails?.contents?.find(c => c.placement === 'HEADLINE')?.descriptions?.[0]?.text?.toLowerCase() || '';
              const typeId = item.type ? item.type.split('#').pop()?.toLowerCase() : '';
              const callToAction = item.showcaseCreativeDetails?.callToAction?.toLowerCase() || '';
              const status = item.status?.toLowerCase() || '';
              
              return (
                (item._id && item._id.toString().includes(keywordLower)) ||
                (typeId && typeId.includes(keywordLower)) ||
                (headlineText && headlineText.includes(keywordLower)) ||
                (callToAction && callToAction.includes(keywordLower)) ||
                (status && status.includes(keywordLower)) ||
                (item.summary && item.summary.toLowerCase().includes(keywordLower)) ||
                (item.topicType && item.topicType.toLowerCase().includes(keywordLower))
              );
            });
          }

          this.allPostsData = filteredData;
          this.totalPostsCount = showcasesData.length;
          this.initialPostsData = filteredData;
          this.fetchShowcaseStatusForCurrentPage(accountId, userId, filteredData);
          this.postLoading = false;
        },
        (error) => {
          this.notifyService.error('Failed to load showcases from API');
          this.allPostsData = [];
          this.totalPostsCount = 0;
          this.initialPostsData = [];
          this.postLoading = false;
          // Try to load locations count even if showcases fail
          this.loadTotalLocationsCount();
        }
      );
  }

  /**
   * Preload locations for the create showcase modal
   * This runs in parallel with loading showcases to avoid loading delay when opening the modal
   */
  private preloadLocationsForCreateModal(userId: number): void {
    if (!userId) {
      return;
    }

    const accountId = this.account?._id;
    if (accountId == null) {
      return; // Locations filter API requires accountId; skip preload when no account selected
    }

    // Preload locations - this will cache them for when the create modal opens
    this.locationsFilterService
      .preloadFilters(accountId, userId)
      .then(() => {
        console.log('✅ Locations preloaded successfully for create modal');
      })
      .catch((err) => {
        console.warn('⚠️ Failed to preload locations (non-critical):', err);
        // Don't show error to user as this is a background optimization
      });
  }

  setSubscriptions() {
    this.broadcastService.subscribe('localpostStarted', (repl) => {
      this.loadData();
    });

    this.broadcastService.subscribe('localpostProcessed', (repl) => {
      this.loadData();
    });

    // Subscribe to account changes via broadcast service as a fallback
    this.broadcastService.subscribe('accountChanged', (repl) => {
      if (repl && repl.ident) {
        this.account = this.dbService.Account.findByIdent(repl.ident);
      } else if (repl && repl.account) {
        this.account = this.dbService.Account.findByIdent(repl.account.ident || repl.account);
      }
    });
  }

  get accountIsNotLeaf() {
    return !this.account?.isLeaf;
  }

  createPostTooltip() {
    if (!this.account?.isLeaf) {
      return 'Please choose a sub-account before creating a post.';
    }
    if (this.appleMapsDisabled) {
      return 'Apple Maps is not configured for this account.';
    }
    return '';
  }

  openPostDelete(rowData) {
    if(!this.checkDeletePerms) return;
    this.dialogService.open(AbpPostDeleteComponent, {
      data: {...rowData},
      width: '40%',
    });
  }

  deleteBtnClass(data) {
    return ( ['DP', 'UR', 'AR'].includes(data._status) ) ? 'disabled' : '';
  }

  openPostCreate() {
    this.createPostRef = this.dialogService.open(EventPostComponent, {
      width: '90%',
    });

    // update posts list after creating the new post
    this.createPostRef.onClose.subscribe(() => {
      // Using static data - reload data instead of API call
      this.loadData();
    });
  }

  openLocations(rowData: any) {
    console.log('showcase_creative_id', rowData?.showcase_creative_id);
    const showcaseId = this.getShowcaseId(rowData);
    const locationIds = rowData.locations || [];
    const isAllLocations = !locationIds || locationIds.length === 0;
    
    this.dialogService.open(AbpPostLocationsComponent, {
      width: '50%',
      data: {
        isPagination: true,
        id: showcaseId,
        locationIds: locationIds,
        isAllLocations: isAllLocations,
        showcaseData: rowData,
        creativeId: rowData?.showcase_creative_id,
      },
    });
  }

  getShowcaseId(rowData: any): string | number {
    if (rowData.type) {
      return rowData.type.split('#').pop() || rowData.type;
    }
    return rowData._id || rowData.id;
  }

  /**
   * Status for table row: from GET /apple/showcases?creative_id=... (showcase_status) when available,
   * otherwise rowData.status or rowData._status.
   */
  getRowStatus(rowData: any): string {
    const creativeId = rowData?.showcase_creative_id;
    if (creativeId != null && this.statusByCreativeId[String(creativeId)]) {
      return this.statusByCreativeId[String(creativeId)];
    }
    return '-';
  }

  /** True when the row status is Suspended (hides "Suspend Showcase" link). */
  isSuspended(rowData: any): boolean {
    const status = this.getRowStatus(rowData);
    return status != null && String(status).toUpperCase() === 'SUSPENDED';
  }

  /** True only when showcase status is PUBLISHED; enables "Suspend Showcase" button. */
  canSuspendShowcase(rowData: any): boolean {
    const status = this.getRowStatus(rowData);
    return status != null && String(status).toUpperCase() === 'PUBLISHED';
  }

  /** True when a deactivate request is in progress for this row. */
  isDeactivating(rowData: any): boolean {
    if (this.deactivatingShowcaseId == null) {
      return false;
    }
    const creativeId = rowData?.showcase_creative_id;
    return creativeId != null && String(creativeId) === String(this.deactivatingShowcaseId);
  }

  /**
   * Opens "Are you sure?" confirmation; on confirm, calls POST /apple/showcases/action to deactivate the showcase.
   * Uses showcase_id from GET /apple/showcases?creative_id=... response.
   */
  openSuspendConfirm(rowData: any): void {
    const creativeId = rowData?.showcase_creative_id != null ? String(rowData.showcase_creative_id) : null;
    if (!creativeId) {
      this.notifyService.error('Showcase creative ID not found.');
      return;
    }
    const showcaseId = this.showcaseIdByCreativeId[creativeId];
    if (showcaseId == null || showcaseId === '') {
      this.notifyService.error('Showcase ID not available. Please refresh and try again.');
      return;
    }
    const confirmed = window.confirm('Are you sure you want to suspend this showcase?');
    if (!confirmed) {
      return;
    }

    const accountId = this.account?._id;
    const userId = this.sessionService.loginObj?._id;
    if (!userId) {
      this.notifyService.error('Authentication required.');
      return;
    }

    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000,
    };
    const jwtToken = generateJWTToken(payloadObj, this.JWT_SECRET);
    console.log('Showcases action API – auth:', {
      authToken: jwtToken,
      accountId,
      userId,
    });
    const headers = new HttpHeaders()
      .set('Authorization', jwtToken)
      .set('Content-Type', 'application/json');

    const body = { action: 'deactivate', showcaseId };
    console.log('Showcases action API – request body:', body);

    this.deactivatingShowcaseId = creativeId;
    this.http
      .post<unknown>(this.SHOWCASES_ACTION_API_URL, body, { headers })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (response) => {
          console.log('Showcases action API – response:', response);
          this.deactivatingShowcaseId = null;
          this.notifyService.success('Showcase suspended.');
          this.loadData();
        },
        (err) => {
          console.log('Showcases action API – error:', err?.error ?? err);
          this.deactivatingShowcaseId = null;
          const message = err?.error?.message || err?.message || 'Failed to suspend showcase.';
          this.notifyService.error(message);
        }
      );
  }

  /**
   * Fetch showcase_status from GET /apple/showcases?creative_id=... for each unique creative_id
   * in the current page and store in statusByCreativeId for the status column.
   */
  private fetchShowcaseStatusForCurrentPage(accountId: number, userId: number, rows: any[]): void {
    const creativeIds = [...new Set(
      (rows || [])
        .map(r => r?.showcase_creative_id)
        .filter(id => id != null && id !== '')
    )] as string[];
    if (creativeIds.length === 0) {
      this.statusByCreativeId = {};
      return;
    }
    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000,
    };
    const jwtToken = generateJWTToken(payloadObj, this.JWT_SECRET);
    const headers = new HttpHeaders()
      .set('Authorization', jwtToken)
      .set('Content-Type', 'application/json');

    const requests = creativeIds.map(id =>
      this.http.get<any>(`${this.SHOWCASES_STATUS_API_BASE}?creative_id=${id}`, { headers })
    );
    forkJoin(requests)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (responses) => {
          const statusMap: Record<string, string> = {};
          const showcaseIdMap: Record<string, string | number> = {};
          responses.forEach((res, i) => {
            const creativeId = String(creativeIds[i]);
            const items = res?.items ?? res?.locations ?? (Array.isArray(res) ? res : []);
            const first = Array.isArray(items) && items.length > 0 ? items[0] : null;
            if (first) {
              const status = first?.showcase_status ?? first?.status;
              if (status) {
                statusMap[creativeId] = String(status);
              }
              const showcaseId = first?.showcase_id;
              if (showcaseId != null && showcaseId !== '') {
                showcaseIdMap[creativeId] = showcaseId;
              }
            }
          });
          this.statusByCreativeId = { ...this.statusByCreativeId, ...statusMap };
          this.showcaseIdByCreativeId = { ...this.showcaseIdByCreativeId, ...showcaseIdMap };
        },
        () => {
          this.statusByCreativeId = {};
          this.showcaseIdByCreativeId = {};
        }
      );
  }

  getSequentialNumber(rowIndex: number): number {
    // rowIndex is 0-based, add 1 for display, and add offset for pagination
    return (this.offset || 0) + rowIndex + 1;
  }

  getHeadlineText(rowData: any): string {
    if (rowData.showcaseCreativeDetails?.contents) {
      const headlineContent = rowData.showcaseCreativeDetails.contents.find((c: any) => c.placement === 'HEADLINE');
      if (headlineContent?.descriptions && headlineContent.descriptions.length > 0) {
        // If multiple languages, format as "Text (locale) / Text (locale)"
        if (headlineContent.descriptions.length > 1) {
          return headlineContent.descriptions
            .map((desc: any) => `${desc.text} (${desc.locale?.toLowerCase() || desc.locale})`)
            .join(' / ');
        } else {
          // Single language, show text with locale
          const desc = headlineContent.descriptions[0];
          return desc.text ? `${desc.text} (${desc.locale?.toLowerCase() || desc.locale})` : '-';
        }
      }
    }
    return rowData.summary || '-';
  }

  getCallToAction(rowData: any): string {
    return rowData.showcaseCreativeDetails?.callToAction || rowData.action || '-';
  }

  getLocationsCount(rowData: any): number {
    // If locations array is empty, null, or undefined, it means all locations are selected
    if (!rowData.locations || rowData.locations.length === 0) {
      return -1; // Use -1 to indicate "all locations"
    }
    return rowData.locations.length;
  }

  getLocationsText(rowData: any): string {
    const count = this.getLocationsCount(rowData);
    // If count is -1, it means all locations are selected (empty array)
    if (count === -1) {
      // Show total locations count if available, otherwise just "View Locations"
      const totalCount = this.totalLocationsCount || 0;
      return totalCount > 0 ? `View Locations (${totalCount})` : 'View Locations';
    }
    return `View Locations (${count})`;
  }

  duplicatePost(rowData) {
    const tempObj = {...rowData};
    tempObj._id = null;
    tempObj._createdAt = null;
    tempObj.status = 'AP';

    this.dialogService.open(SuccessComponent, {
      width: '20%',
    });
    this.abpService.updateLocalPost(tempObj);
  }

  unpublishPost(rowData) {
    // temporarily disabled
    // const tempObj = { ...rowData, _status: 'UP' };
    // this.dialogService.open(SuccessComponent, {
    //   width: '20%',
    // });
    // this.gbpService.updateLocalPost(tempObj);
  }

  editPost(rowData) {
    if(!this.checkEditPerms) return;
    this.dialogService.open(AbpPostEditComponent, {
      width: '90%',
      data: {...rowData},
    });

  }

  onChipsRefresh(event) {
    this.chipsArray = [].concat.apply([], Object.values(event)); // merge all checkbox values into one array
    this.chipsArray = this.chipsArray.filter((e) => e); // remove all possible undefined
    this.filters = event;
    this.loadData();
  }

  ngOnDestroy(): void {
    this.tableFilterService.clearFilters(); // clear table filters
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private buildWhereClause(): Array<string> {
    const filterClauses: string[][] = [];
    const tempFilterArr: string[] = [];
    const isFiltersApplied = this.chipsArray.length > 0;

    const pushQueriesToMainArray = () => {
      let filterClause: Array<string> = ['-or'];
      filterClause = filterClause.concat(tempFilterArr);
      filterClauses.push(filterClause);
      tempFilterArr.length = 0;
    }

    if (isFiltersApplied) {
      for (const [key, value] of Object.entries(this.filters)) {
        if (value.length > 0 && value[0] != undefined) {
          switch (key) {
            case 'ATTRIBUTES':
              value.forEach((element: string) => {
                switch (element) {
                  case 'Scheduled':
                    tempFilterArr.push("_status IN ('AP', 'UP','DP')");
                    break;
                  case 'Published':
                    tempFilterArr.push("_status = 'A'");
                    break;
                  case 'Unpublished':
                    tempFilterArr.push("_status = 'U'");
                    break;
                }
              });
              pushQueriesToMainArray();
              break;
            case 'POST':
              value.forEach((element: string) => tempFilterArr.push(`topicType = '${element}'`));
              pushQueriesToMainArray();
              break;
            case 'DATE':
              value.forEach((element: string) => {
                switch (element) {
                  case 'This month':
                    tempFilterArr.push(this.abpFiltersDateService.inThisMonth('(SELECT min(localPostUpdatedAt) FROM localpostdetailgmb d WHERE d.post_id = localpostgmb._id)'));
                    break;
                  case 'Last month':
                    tempFilterArr.push(this.abpFiltersDateService.inLastMonth('(SELECT min(localPostUpdatedAt) FROM localpostdetailgmb d WHERE d.post_id = localpostgmb._id)'));
                    break;
                  default:
                    const dateArray: string[] = element.split(' - ');
                    tempFilterArr.push(`localpostcreatetime >= '${dateArray[0]}' AND localpostcreatetime < '${dateArray[1]}'`);
                    break;
                }
              });
              pushQueriesToMainArray();
              break;
          }
        }
      }
    }

    if (this.keyword) {
      tempFilterArr.push(`b._id::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`b.summary::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`b.topicType::text ~* '\\y${this.keyword}'`);
      tempFilterArr.push(`l.storeCode::text ~* '\\y${this.keyword}'`);
      pushQueriesToMainArray();
    }

    let whereClauseArray: Array<any> = ['-and'];
    whereClauseArray = whereClauseArray.concat(filterClauses);
    return whereClauseArray;
  }

  get checkAddPerms(): boolean {
    return this.sessionService.hasPerm('post', 'a');
  }

  /**
   * Same logic as Reports tab "APPLE MAPS" disabled: true when Apple Maps is not configured
   * for this account (in non-local/non-development). Used to disable CREATE SHOWCASE when
   * the account would have APPLE MAPS tab disabled in Reports.
   */
  get appleMapsDisabled(): boolean {
    if (!this.account) {
      return false;
    }
    const global = typeof this.account.global === 'string' ? JSON.parse(this.account.global || '{}') : this.account.global;
    const { publish: applePublishStatus = false, businessId: appleBusinessId = undefined } = global?.appleMaps || {};
    const isDev = environment.name === 'local' || environment.name === 'development';
    return !isDev && !(applePublishStatus && appleBusinessId);
  }

  get checkEditPerms(): boolean {
    return this.sessionService.hasPerm('post', 'e');
  }

  get checkDeletePerms(): boolean {
    return this.sessionService.hasPerm('post', 'd');
  }

}
